import { Button, Form, FormItemProps, Input, Select } from 'antd';
import { FilterIdType } from 'views/Refund/utils/constants';
import { useTranslate } from '@tolgee/react';
import { useMemo, useRef, useState } from 'react';
import style from 'views/Conversation/ConvList/SearchStyle';
import { useSize } from 'ahooks';
import { CloseCircleFilled } from '@ant-design/icons';
import { uniq } from 'lodash-es';

interface SnFormFieldProps {
  platform: General.PlatformUpperCase;
  filterType?: 'return' | 'cancel';
  validate?: boolean;
  onValuesChange?: (values: any) => void;
}

const supportTrackingNumberPlatforms: General.PlatformUpperCase[] = [
  'SHOPEE',
  'LAZADA',
  'TIKTOK',
  'TIKTOK_US',
  'MERCADO',
];

export const SnFormField = (props: SnFormFieldProps) => {
  const { platform, filterType, validate = true, onValuesChange } = props;
  const { t } = useTranslate();

  const [isBatch, setIsBatch] = useState(false);

  const selectWrapperRef = useRef<HTMLDivElement>(null);
  const { width: selectWidth = 0 } = useSize(selectWrapperRef) || {};

  const [inputValue, setInputValue] = useState('');

  const rules: FormItemProps['rules'] = useMemo(
    () => [
      {
        type: 'string',
        pattern: /^\w+$/,
        message: t('scene.orderWorkspace.returnRefundTab.filter.validation.alphanumericOnly'),
      },
    ],
    [t],
  );

  const supportBatchTypes = [
    FilterIdType.orderId,
    FilterIdType.returnId,
    FilterIdType.returnTrackingNumber,
  ];

  const typeSelect = (
    <Form.Item noStyle shouldUpdate>
      {({ setFieldsValue, getFieldsValue }) => {
        const values = getFieldsValue();
        return (
          <>
            <Form.Item name="snType" noStyle />
            <Select
              style={{ maxWidth: '10vw' }}
              dropdownMatchSelectWidth={false}
              disabled={filterType === 'cancel'}
              value={values.snType}
              onChange={(value) => {
                setFieldsValue({
                  ...values,
                  snType: value,
                });
                if (!supportBatchTypes.includes(value) && isBatch) {
                  setIsBatch(false);
                }
              }}
            >
              <Select.Option value={FilterIdType.orderId}>
                {t(`filter.${FilterIdType.orderId}`)}
              </Select.Option>
              <Select.Option value={FilterIdType.returnId}>
                {t(`filter.${FilterIdType.returnId}`)}
              </Select.Option>
              {supportTrackingNumberPlatforms.includes(platform) && (
                <Select.Option value={FilterIdType.returnTrackingNumber}>
                  {t('scene.orderWorkspace.returnRefundTab.filter.reverseTrackingNumber')}
                </Select.Option>
              )}
              {platform === 'SHOPEE' && (
                <>
                  <Select.Option value={FilterIdType.itemName}>
                    {t('scene.orderWorkspace.returnRefundTab.filter.productName')}
                  </Select.Option>
                  <Select.Option value={FilterIdType.skuName}>SKU</Select.Option>
                  <Select.Option value={FilterIdType.sellerSku}>
                    {t('scene.orderWorkspace.returnRefundTab.filter.articleNumber')}
                  </Select.Option>
                  <Select.Option value={FilterIdType.buyerNick}>
                    {t('scene.orderWorkspace.returnRefundTab.filter.buyerNickname')}
                  </Select.Option>
                </>
              )}
            </Select>
          </>
        );
      }}
    </Form.Item>
  );

  return isBatch ? (
    <Form.Item noStyle shouldUpdate>
      {({ getFieldsValue, setFieldsValue }) => {
        const handleValuesChange = (values: any) => {
          setFieldsValue(values);
          onValuesChange?.(values);
        };
        const values = getFieldsValue();
        return (
          <div className={style.batchSearch}>
            <Form.Item noStyle name="sn" />
            <div className={style.searchIpt}>
              <section ref={selectWrapperRef}>{typeSelect}</section>
              <Input.TextArea
                autoFocus
                style={{ paddingLeft: selectWidth + 4, paddingRight: 18 }}
                rows={5}
                placeholder={t(
                  'scene.orderWorkspace.returnRefundTab.filter.batchSearchPlaceholder',
                  { x: 50 },
                )}
                value={inputValue}
                onChange={({ target: { value } }) => {
                  setInputValue(value);
                  const splits = [' ', '，', '\r', '\n', ',', '	'];
                  const sn = splits
                    .reduce<string>((res, split) => {
                      return uniq(res.split(split).filter((_) => _)).join(',');
                    }, value || '')
                    .split(',')
                    .slice(0, 50)
                    .join(',');
                  handleValuesChange({
                    ...values,
                    sn: sn,
                  });
                }}
              />
              <CloseCircleFilled
                className="!right-2"
                onClick={() => {
                  setInputValue('');
                  handleValuesChange({
                    ...values,
                    sn: '',
                  });
                }}
              />
            </div>
            <Button
              size="small"
              type="link"
              onClick={() => {
                setInputValue('');
                handleValuesChange({
                  ...values,
                  sn: '',
                });
                setIsBatch(false);
              }}
            >
              {t('scene.orderWorkspace.returnRefundTab.filter.cancelSearch')}
            </Button>
          </div>
        );
      }}
    </Form.Item>
  ) : (
    <Form.Item noStyle shouldUpdate>
      {({ getFieldValue }) => {
        const supportBatch =
          filterType === 'return' && supportBatchTypes.includes(getFieldValue('snType'));
        return (
          <Form.Item name="sn" rules={validate ? rules : undefined}>
            <Input
              allowClear
              placeholder={
                supportBatch
                  ? t('scene.orderWorkspace.returnRefundTab.filter.doubleClickBatchSearch')
                  : t('scene.orderWorkspace.returnRefundTab.filter.pleaseEnter')
              }
              addonBefore={typeSelect}
              onDoubleClick={() => {
                supportBatch && setIsBatch(true);
                setInputValue(getFieldValue('sn'));
              }}
            />
          </Form.Item>
        );
      }}
    </Form.Item>
  );
};
